import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Trash2, Plus, Instagram, Youtube, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import { youtubeEmbed, instagramEmbed, youtubeId, instagramId } from "@/lib/social-embed";

export const Route = createFileRoute("/admin/social")({
  head: () => ({ meta: [{ title: "Social Videos — Admin" }] }),
  component: AdminSocial,
});

type Video = {
  id: string;
  platform: "instagram" | "youtube";
  url: string;
  title: string;
  sort_order: number;
  active: boolean;
};

function AdminSocial() {
  const [videos, setVideos] = useState<Video[]>([]);
  const [platform, setPlatform] = useState<"youtube" | "instagram">("youtube");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data, error } = await supabase
      .from("social_videos" as never)
      .select("*")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) return toast.error(error.message);
    setVideos((data as unknown as Video[]) ?? []);
  };

  useEffect(() => { load(); }, []);

  const trimmed = url.trim();
  const isValid = trimmed
    ? platform === "youtube" ? !!youtubeId(trimmed) : !!instagramId(trimmed)
    : false;

  const add = async () => {
    if (!trimmed) return toast.error("Paste a video URL");
    if (!isValid) {
      return toast.error(
        platform === "instagram"
          ? "Paste a full Instagram POST or REEL URL (e.g. https://www.instagram.com/reel/XXXXX/). Profile handles like @name can't be embedded."
          : "Paste a full YouTube URL (e.g. https://www.youtube.com/watch?v=XXXX or /shorts/XXXX)."
      );
    }
    setBusy(true);
    const next = (videos[videos.length - 1]?.sort_order ?? 0) + 10;
    const { error } = await supabase.from("social_videos" as never).insert({
      platform, url: trimmed, title: title.trim(), sort_order: next, active: true,
    } as never);
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Video added");
    setUrl(""); setTitle("");
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("social_videos" as never).delete().eq("id", id);
    if (error) return toast.error(error.message);
    setVideos((v) => v.filter((x) => x.id !== id));
  };

  const toggle = async (v: Video) => {
    const { error } = await supabase.from("social_videos" as never).update({ active: !v.active } as never).eq("id", v.id);
    if (error) return toast.error(error.message);
    setVideos((list) => list.map((x) => (x.id === v.id ? { ...x, active: !v.active } : x)));
  };

  const move = async (v: Video, dir: -1 | 1) => {
    const idx = videos.findIndex((x) => x.id === v.id);
    const swap = videos[idx + dir];
    if (!swap) return;
    const a = v.sort_order, b = swap.sort_order;
    await supabase.from("social_videos" as never).update({ sort_order: b } as never).eq("id", v.id);
    await supabase.from("social_videos" as never).update({ sort_order: a } as never).eq("id", swap.id);
    load();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="display text-xl">Social Videos</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Paste Instagram or YouTube links. They'll auto-play (muted) and loop on the storefront.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        <div className="flex gap-2">
          <button
            onClick={() => setPlatform("youtube")}
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border ${platform === "youtube" ? "bg-foreground text-background border-foreground" : "border-border"}`}
          ><Youtube className="h-4 w-4" /> YouTube</button>
          <button
            onClick={() => setPlatform("instagram")}
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded border ${platform === "instagram" ? "bg-foreground text-background border-foreground" : "border-border"}`}
          ><Instagram className="h-4 w-4" /> Instagram</button>
        </div>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={platform === "youtube" ? "https://www.youtube.com/watch?v=… or /shorts/…" : "https://www.instagram.com/reel/…"}
          className={`w-full border bg-background px-3 py-2 rounded text-sm ${trimmed && !isValid ? "border-destructive" : "border-border"}`}
        />
        {trimmed && !isValid && (
          <p className="text-xs text-destructive">
            {platform === "instagram"
              ? "Use the full URL of a post or reel — handles like @name can't be embedded."
              : "Use the full video URL (watch?v=… or /shorts/…)."}
          </p>
        )}
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Optional caption"
          className="w-full border border-border bg-background px-3 py-2 rounded text-sm"
        />
        <button
          onClick={add}
          disabled={busy || !isValid}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-foreground text-background rounded disabled:opacity-40"
        ><Plus className="h-4 w-4" /> Add video</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {videos.length === 0 && (
          <p className="md:col-span-2 p-5 text-sm text-muted-foreground border border-border rounded-lg bg-card">No videos yet.</p>
        )}
        {videos.map((v, i) => {
          const src = v.platform === "youtube"
            ? youtubeEmbed(v.url, { autoplay: true, mute: true, loop: true })
            : instagramEmbed(v.url);
          return (
            <div key={v.id} className={`rounded-lg border border-border bg-card overflow-hidden ${!v.active ? "opacity-50" : ""}`}>
              <div className="aspect-[9/16] bg-muted">
                {src ? (
                  <iframe
                    src={src}
                    title={v.title || v.url}
                    className="w-full h-full"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="grid place-items-center h-full text-xs text-muted-foreground">Invalid URL</div>
                )}
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  {v.platform === "youtube" ? <Youtube className="h-3.5 w-3.5" /> : <Instagram className="h-3.5 w-3.5" />}
                  <span className="truncate flex-1">{v.title || v.url}</span>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <button onClick={() => move(v, -1)} disabled={i === 0} className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded disabled:opacity-40"><ArrowUp className="h-3 w-3" /></button>
                  <button onClick={() => move(v, 1)} disabled={i === videos.length - 1} className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded disabled:opacity-40"><ArrowDown className="h-3 w-3" /></button>
                  <button onClick={() => toggle(v)} className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded">
                    {v.active ? <><Eye className="h-3 w-3" /> Visible</> : <><EyeOff className="h-3 w-3" /> Hidden</>}
                  </button>
                  <button onClick={() => remove(v.id)} className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded text-destructive ml-auto"><Trash2 className="h-3 w-3" /> Delete</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
